import type { TutorLegalStore } from './interfaces'
import type { TutorBilling, TutorLegalForm } from '@ts-tutor-legal/interfaces'
import type { DropZoneAcceptedFile, DropZoneItemPayload } from '@UI/Other/DropZone'

import { TutorBillingAccountType } from '@ts-lists/enums'

import { TUTOR_LEGAL_FIELD_MAP, TUTOR_LEGAL_ACCOUNT_FIELDS, TUTOR_LEGAL_OPTIONAL_FIELDS } from '@config/tutor-legal'
import { isValidEmail, isValidPhone } from '@helpers/validation'

const buildEmptyForm = (): TutorLegalForm =>
  Object.fromEntries(TUTOR_LEGAL_FIELD_MAP.map(entry => [entry.form, ''])) as unknown as TutorLegalForm

const mapBillingToForm = (billing: TutorBilling): TutorLegalForm => {
  const form = buildEmptyForm()

  TUTOR_LEGAL_FIELD_MAP.forEach(({ form: formKey, section, api }) => {
    const source = billing[section] as unknown as Record<string, string | null>
    form[formKey] = source[api] ?? ''
  })

  return form
}

export const actions = {
  reset (this: TutorLegalStore): void {
    this.billing = null
    this.form = buildEmptyForm()
    this.errors = {}
    this.existingFiles = []
    this.pendingFiles = []
  },
  setBilling (this: TutorLegalStore, payload: TutorLegalStore['billing']): void {
    this.billing = payload
    this.existingFiles = payload?.file_pack?.files ?? []
    this.form = payload ? mapBillingToForm(payload) : buildEmptyForm()
    this.errors = {}
    // Replacing billing state invalidates queued uploads too — otherwise
    // unsaved files picked for the previous state would ride along into the
    // next save (the save flow rebuilds its file list from existing + pending).
    this.pendingFiles = []
  },
  removeExistingFile (this: TutorLegalStore, id: number): void {
    this.existingFiles = this.existingFiles.filter(file => file.id !== id)
  },
  setLoading (this: TutorLegalStore, payload: TutorLegalStore['loading']): void {
    this.loading = payload
  },
  setFormField (this: TutorLegalStore, key: keyof TutorLegalStore['form'], value: string): void {
    this.form[key] = value
    Reflect.deleteProperty(this.errors, key)
    if (key === 'payment_service') Reflect.deleteProperty(this.errors, 'account')
  },
  validate (this: TutorLegalStore, accountType: TutorBillingAccountType | null): boolean {
    const keys = Object.keys(this.form) as (keyof TutorLegalStore['form'])[]
    const errors: TutorLegalStore['errors'] = {}

    keys.forEach((key) => {
      if (TUTOR_LEGAL_OPTIONAL_FIELDS.includes(key)) return
      if (!this.form[key]) errors[key] = ''
    })

    const account = this.form.account
    if (account && accountType === TutorBillingAccountType.Phone && !isValidPhone(account)) {
      errors.account = TUTOR_LEGAL_ACCOUNT_FIELDS[TutorBillingAccountType.Phone].error
    }
    if (account && accountType === TutorBillingAccountType.Email && !isValidEmail(account)) {
      errors.account = TUTOR_LEGAL_ACCOUNT_FIELDS[TutorBillingAccountType.Email].error
    }

    this.errors = errors
    return Object.keys(errors).length === 0
  },
  acceptFiles (this: TutorLegalStore, files: DropZoneAcceptedFile[]): void {
    this.pendingFiles = [...this.pendingFiles, ...files]
  },
  removePendingFile (this: TutorLegalStore, payload: DropZoneItemPayload): void {
    this.pendingFiles = this.pendingFiles.filter(file => file.id !== payload.id)
  },
  resetPending (this: TutorLegalStore): void {
    this.pendingFiles = []
  },
}
